import { Fragment, useEffect, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon, MagnifyingGlassIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { zohoBooksService, type ZohoCustomer } from '../../services/zohoBooksService';

interface ZohoCustomerImportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onImported: () => void;
}

export function ZohoCustomerImportModal({ isOpen, onClose, onImported }: ZohoCustomerImportModalProps) {
  const [customers, setCustomers] = useState<ZohoCustomer[]>([]);
  const [loading, setLoading] = useState(false);
  const [importingId, setImportingId] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (isOpen) void loadCustomers();
  }, [isOpen]);

  const loadCustomers = async (searchTerm: string = '') => {
    setLoading(true);
    try {
      const { customers } = await zohoBooksService.listCustomers({ search: searchTerm });
      setCustomers(customers);
    } catch (error) {
      const msg = error instanceof Error ? error.message : 'Failed to load customers';
      toast.error(msg);
      setCustomers([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    void loadCustomers(search.trim());
  };

  const handleImport = async (customer: ZohoCustomer) => {
    setImportingId(customer.contact_id);
    const toastId = toast.loading(`Importing ${customer.contact_name}…`);
    try {
      const result = await zohoBooksService.importCustomer(customer.contact_id);
      const contactNote = result.contacts_imported > 0
        ? ` (${result.contacts_imported} contact${result.contacts_imported === 1 ? '' : 's'})`
        : '';
      toast.success(`Imported ${result.client.name}${contactNote}`, { id: toastId });
      onImported();
    } catch (error) {
      const msg = error instanceof Error ? error.message : 'Import failed';
      toast.error(msg, { id: toastId });
    } finally {
      setImportingId(null);
    }
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/40" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-3xl transform overflow-hidden rounded-2xl bg-white shadow-xl transition-all">
                <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
                  <Dialog.Title className="text-lg font-semibold text-gray-900">
                    Import from Zoho Books
                  </Dialog.Title>
                  <button
                    onClick={onClose}
                    className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </div>

                <form onSubmit={handleSearch} className="border-b border-gray-200 px-6 py-3">
                  <div className="flex items-center gap-2">
                    <div className="relative flex-1">
                      <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                      <input
                        type="text"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Search Zoho customers by name or email…"
                        className="w-full rounded-md border border-gray-300 py-2 pl-9 pr-3 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                      />
                    </div>
                    <button
                      type="submit"
                      disabled={loading}
                      className="rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50"
                    >
                      Search
                    </button>
                  </div>
                </form>

                <div className="max-h-[60vh] overflow-y-auto">
                  {loading ? (
                    <div className="flex items-center justify-center gap-2 px-6 py-16 text-sm text-gray-500">
                      <ArrowPathIcon className="h-5 w-5 animate-spin" />
                      Loading customers…
                    </div>
                  ) : customers.length === 0 ? (
                    <div className="px-6 py-16 text-center text-sm text-gray-500">
                      No customers found.
                    </div>
                  ) : (
                    <ul className="divide-y divide-gray-100">
                      {customers.map((c) => {
                        const subtitle = [c.company_name, c.email, c.phone || c.mobile]
                          .filter(Boolean)
                          .join(' · ');
                        const cityState = [c.billing_address?.city, c.billing_address?.state]
                          .filter(Boolean)
                          .join(', ');
                        return (
                          <li key={c.contact_id} className="flex items-center justify-between gap-4 px-6 py-3 hover:bg-gray-50">
                            <div className="min-w-0 flex-1">
                              <p className="truncate text-sm font-medium text-gray-900">
                                {c.contact_name || c.company_name || '(unnamed)'}
                              </p>
                              {subtitle && (
                                <p className="truncate text-xs text-gray-500">{subtitle}</p>
                              )}
                              {cityState && (
                                <p className="truncate text-xs text-gray-400">{cityState}</p>
                              )}
                            </div>
                            <button
                              onClick={() => handleImport(c)}
                              disabled={importingId === c.contact_id}
                              className="shrink-0 rounded-md border border-primary-600 px-3 py-1.5 text-sm font-medium text-primary-700 hover:bg-primary-50 disabled:opacity-50"
                            >
                              {importingId === c.contact_id ? 'Importing…' : 'Import'}
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
